//-- Copyright (c) 2021 TMT Insights Group, LLC
import { ColumnConfig } from '@/__configs/config_types';
import { insertWordBreaks } from '@/_utils/utils';
import { OrderJob, OrderJobAsset, UserGroup, UserProfile } from '@/redux/slices/slice_types';
import { Box, Typography } from '@mui/material';
import {
  AssigneeSelect,
  FileTypeDisplay,
  History,
  LabelValue,
  LinkAsset,
  Notes,
  PendingAssetRowExpand,
  StatusDropdown
} from '../../../../exports/columnCommonComponents';

const columns: Record<string, ColumnConfig<OrderJobAsset, any>> = {
  expandable: {
    label: '',
    isExpandable: true,
    render: (props: { item: OrderJob; isCollapsed: boolean }) => (
      <PendingAssetRowExpand {...props}>
        <>
          <LabelValue label="Deliverable Type">{props?.item?.component_deliverable_type || '-'}</LabelValue>
        </>
      </PendingAssetRowExpand>
    )
  },
  hid: {
    label: 'Task ID',
    sortField: 'id',
    render: (cell: OrderJobAsset['hid']) => <strong>{cell}</strong>
  },
  jobs: {
    label: 'Title Name',
    sortField: 'jobs__title__meta__series_title',
    minWidth: 350,
    render: (cell: OrderJobAsset['jobs'], row: OrderJobAsset) => {
      if (!cell) return '-';
      const title = cell?.[0]?.title;
      if (!title) return '-';
      const { entity_type = 'feature', name = '', sequence_order, lineage_meta = {} } = title;
      return (
        <div style={{ lineHeight: 1.5 }}>
          <div>
            <strong>{insertWordBreaks(lineage_meta.series_name || name)}</strong>
          </div>
          <Typography variant="subtitle2" sx={{ fontSize: '.95rem', lineHeight: 1.4, mt: 0.5, opacity: 0.5, fontWeight: 500 }}>
            {entity_type === 'episode' && (
              <>
                S{lineage_meta.season_sequence_order} &bull; E{sequence_order} &bull;
              </>
            )}
            {lineage_meta.series_name && <>&quot;{name}&quot;</>}
          </Typography>
          {row.version?.name && (
            <>
              <Typography variant="caption" sx={{ color: '#667799' }}>
                {row.version.name.charAt(0).toUpperCase() + row.version.name.slice(1)}
              </Typography>
            </>
          )}
        </div>
      );
    }
  },
  type: {
    label: 'Type',
    render: (cell: OrderJobAsset['type']) => (cell ? <FileTypeDisplay type={cell} /> : '-')
  },
  version__external_id: {
    label: 'External ID'
  },
  component_deliverable_type: {
    label: 'Deliverable Type'
  },
  assigned_group: {
    label: 'Assigned Group',
    render: (cell: UserGroup, row: OrderJobAsset) => {
      return <AssigneeSelect value={cell} id={row.id} assigns="group" isAsset />;
    },
    minWidth: 200,
    disableSort: true
  },
  assigned_user: {
    label: 'Assignee',
    render: (cell: UserProfile, row: OrderJobAsset) => <AssigneeSelect value={cell} id={row.id} assigns="user" isAsset />,
    minWidth: 200,
    disableSort: true
  },
  status: {
    label: 'Status',
    render: (cell: OrderJobAsset['status'], row: OrderJobAsset) => <StatusDropdown id={row.id} status={cell} />
  },
  history: {
    label: 'History',
    disableSort: true,
    render: (_cell: any, row: OrderJobAsset) => (
      <Box sx={{ ml: 1 }}>
        <History data={row} iconOnly />
      </Box>
    )
  },
  notes: {
    label: 'Notes',
    render: (_cell: any, row: OrderJobAsset) => (
      <div style={{ width: 40, textAlign: 'center' }}>
        <Notes data={row} type="jobs" />
      </div>
    ),
    disableSort: true
  },
  link_action: {
    label: 'Link',
    disableSort: true,
    render: (_cell: any, row: OrderJobAsset) => <LinkAsset disabled={row.status !== 'awaiting_ingest'} data={row} />
  }
};

export default columns;
